/* ============================================================================
   domain_selector — builder tree types + the EDE-domain wire shape.
   Foundation.presentation Enhancement 13. Frozen UX:
   roadmap/foundation/presentation/enhancements/assets/13-domain-selector-mockup/
   ========================================================================== */

/** A leaf domain term: [field-chain, operator, value]. */
export type DomainLeaf = [string, string, DomainScalar];
/** Logical prefix operators used by EDE domains. */
export type DomainOperator = "&" | "|" | "!";
/** A full EDE domain (prefix notation, implicit-AND between operands). */
export type DomainTerm = DomainLeaf | DomainOperator;
export type Domain = DomainTerm[];

/** Scalar value stored in a leaf (incl. `$principal.*` tokens kept verbatim). */
export type DomainScalar = string | number | boolean | null | Array<string | number>;

/** A date/datetime range value for the `between` pseudo-operator. */
export interface RangeValue {
    from: string;
    to: string;
}

/** A value as held in the builder tree (range is only used by `between`). */
export type ConditionValue = DomainScalar | RangeValue;

export interface Condition {
    kind: "cond";
    /** Dotted field chain, e.g. "customer_id.country_id.name". */
    field: string;
    /** Builder operator (incl. pseudo-ops `set` / `unset` / `between`). */
    op: string;
    value: ConditionValue;
    /** True when `value` is a `$principal.*` (or `$today`) dynamic token. */
    dyn?: boolean;
}

export interface Group {
    kind: "group";
    join: "AND" | "OR";
    items: Node[];
}

export type Node = Condition | Group;

/** Thrown when a domain shape can't be represented in the visual builder
 *  (e.g. a `!` negating a whole group). Callers fall back to the Code tab. */
export class DomainParseError extends Error {
    constructor(message: string) {
        super(message);
        this.name = "DomainParseError";
    }
}

export const isRange = (v: ConditionValue): v is RangeValue =>
    typeof v === "object" && v !== null && !Array.isArray(v) && "from" in v && "to" in v;

export const isDynToken = (v: unknown): v is string =>
    typeof v === "string" && v.startsWith("$");
